import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { useUpdateFullNameMutation } from "../../../../../../service/user-service"
import { updateCustomerInfo } from "../../../../../../stores/slices/current-user-slice"
import styles from "./edit-account-name-modal.module.scss"
import { RootState } from "../../../../../../stores/store"

interface EditAccountNameModalProps {
  isOpen: boolean
  onClose: () => void
  firstName: string
  lastName: string
}

interface FormValues {
  firstName: string
  lastName: string
}

export const EditAccountNameModal = ({
  isOpen,
  onClose,
  firstName,
  lastName
}: EditAccountNameModalProps) => {
  const { t } = useTranslation()
  const userInfo = useSelector((state: RootState) => state.user)
  const dispatch = useDispatch()
  const [updateFullName, { isLoading }] = useUpdateFullNameMutation()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      firstName,
      lastName
    }
  })

  useEffect(() => {
    if (isOpen) {
      reset({ firstName, lastName })
    }
  }, [isOpen, firstName, lastName, reset])

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await updateFullName({
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim()
      }).unwrap()

      dispatch(updateCustomerInfo({
        firstName: response.firstName,
        lastName: response.lastName,
        countryCode: userInfo.countryCode,
        phoneNumber: userInfo.phoneNumber!,
        hasPin: userInfo.hasPin || false,
        image: userInfo.image || "",
        email: userInfo.email || "",
        customerType: userInfo.customerType
      }))

      toast.success(t("account.web.accountNameUpdated"))
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToUpdateAccountName"))
    }
  }

  const handleCancel = () => {
    reset({ firstName, lastName })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
            aria-label={t("account.web.close")}
          >
            ×
          </button>
          <h2 className={styles.modalTitle}>{t("solar.fullName")}</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.body}>
            <div className={styles.fieldsCard}>
              <div className={styles.inputField}>
                <label className={styles.inputLabel}>{t("common.labels.firstName")}</label>
                <input
                  type="text"
                  className={`${styles.input} ${errors.firstName ? styles.inputError : ""}`}
                  placeholder={t("account.web.enterFirstName")}
                  disabled={isLoading}
                  {...register("firstName", {
                    required: t("common.validation.firstNameRequired"),
                    minLength: {
                      value: 2,
                      message: t("account.web.firstNameMinLength")
                    }
                  })}
                />
                {errors.firstName && <p className={styles.errorText}>{errors.firstName.message}</p>}
              </div>

              <div className={styles.inputField}>
                <label className={styles.inputLabel}>{t("common.labels.surname")}</label>
                <input
                  type="text"
                  className={`${styles.input} ${errors.lastName ? styles.inputError : ""}`}
                  placeholder={t("account.web.enterSurname")}
                  disabled={isLoading}
                  {...register("lastName", {
                    required: t("account.web.surnameRequired"),
                    minLength: {
                      value: 2,
                      message: t("account.web.surnameMinLength")
                    }
                  })}
                />
                {errors.lastName && <p className={styles.errorText}>{errors.lastName.message}</p>}
              </div>
            </div>

            <button type="submit" className={styles.saveButton} disabled={isLoading}>
              {isLoading ? t("account.web.saving") : t("account.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
